import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  adminIntegrationsRepository,
  type ChatConnectionState,
  type ChatConnectLinkCreateResponse,
  type ChatProvider,
  type IntegrationCompany,
} from '@web/data/repositories';
import {
  AppIcons,
  Button,
  Input,
  Kv,
  KvGrid,
  Textarea,
  useOrgContext,
  useSnackbar,
} from '@web/ui/core';
import { t } from '@web/i18n';

import {
  chipStateOf,
  ChatStatusChip,
  formatDate,
  formatDateTime,
  integrationErrorMessage,
  integrationsKey,
  TOOL,
  useIntegrationsAccess,
} from './integrations_shared';

type Snackbar = ReturnType<typeof useSnackbar>['showSnackbar'];
type Link = Extract<ChatConnectionState, { status: 'pending' }>['link'];
type Installation = Extract<ChatConnectionState, { status: 'connected' }>['installation'];

async function copy(text: string, showSnackbar: Snackbar): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showSnackbar({ severity: 'success', message: t('integrations.toast.copied') });
  } catch {
    showSnackbar({ severity: 'error', message: t('integrations.toast.copyError') });
  }
}

function useToolActions(
  provider: ChatProvider,
  groupId: string,
  onIssued: (r: ChatConnectLinkCreateResponse) => void,
) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { orgId } = useOrgContext();
  const access = useIntegrationsAccess();
  const repo = adminIntegrationsRepository;
  const done = (message: string) => async () => {
    await queryClient.invalidateQueries({ queryKey: integrationsKey });
    showSnackbar({ severity: 'success', message });
  };
  const onError = (err: unknown) => {
    showSnackbar({ severity: 'error', message: integrationErrorMessage(err) });
  };
  return {
    showSnackbar,
    ...access,
    issue: useMutation({
      mutationFn: () => repo.issueLink(orgId, provider, groupId),
      onSuccess: async (issued) => {
        onIssued(issued);
        await done(t('integrations.toast.linkIssued'))();
      },
      onError,
    }),
    cancel: useMutation({
      mutationFn: () => repo.cancelLink(orgId, provider, groupId),
      onSuccess: done(t('integrations.toast.linkCancelled')),
      onError,
    }),
    confirm: useMutation({
      mutationFn: () => repo.confirmReview(orgId, provider, groupId),
      onSuccess: done(t('integrations.toast.reviewConfirmed')),
      onError,
    }),
    reject: useMutation({
      mutationFn: () => repo.rejectReview(orgId, provider, groupId),
      onSuccess: done(t('integrations.toast.reviewRejected')),
      onError,
    }),
    disconnect: useMutation({
      mutationFn: () => repo.disconnect(orgId, provider, groupId),
      onSuccess: done(t('integrations.toast.disconnected', { tool: TOOL[provider].name() })),
      onError,
    }),
  };
}

type Actions = ReturnType<typeof useToolActions>;

function Actions({ children }: Readonly<{ children: ReactNode }>): React.JSX.Element {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}

function LinkReveal({
  provider,
  company,
  issued,
  showSnackbar,
}: Readonly<{
  provider: ChatProvider;
  company: IntegrationCompany;
  issued: ChatConnectLinkCreateResponse;
  showSnackbar: Snackbar;
}>): React.JSX.Element {
  const message = t(`integrations.${provider}.message`, {
    group: company.name,
    url: issued.url,
    expires: formatDate(issued.link.expiresAt),
  });
  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-primary-tint bg-paper p-3">
      <p className="m-0 text-meta text-foreground">{t('integrations.tool.reveal.once')}</p>
      <div className="flex gap-2">
        <Input readOnly value={issued.url} aria-label={t('integrations.tool.reveal.linkLabel')} />
        <Button
          variant="outline"
          onClick={() => {
            void copy(issued.url, showSnackbar);
          }}
        >
          <AppIcons name="copy" size={14} />
          {t('integrations.tool.reveal.copyLink')}
        </Button>
      </div>
      <Textarea
        readOnly
        rows={9}
        value={message}
        aria-label={t('integrations.tool.reveal.messageLabel')}
      />
      <Actions>
        <Button
          variant="primary"
          onClick={() => {
            void copy(message, showSnackbar);
          }}
        >
          <AppIcons name="copy" size={14} />
          {t('integrations.tool.reveal.copyMessage')}
        </Button>
      </Actions>
    </div>
  );
}

function PendingBody({
  link,
  revealed,
  actions,
}: Readonly<{ link: Link; revealed: ReactNode; actions: Actions }>) {
  return (
    <>
      <KvGrid>
        <Kv label={t('integrations.tool.pending.issued')}>
          {t('integrations.tool.pending.issuedBy', {
            date: formatDateTime(link.createdAt),
            by: link.createdBy,
          })}
        </Kv>
        <Kv label={t('integrations.tool.pending.expires')}>{formatDate(link.expiresAt)}</Kv>
      </KvGrid>
      {revealed ?? (
        <p className="m-0 text-meta text-muted-foreground">
          {link.expired
            ? t('integrations.tool.pending.expiredNote')
            : t('integrations.tool.pending.lostLink')}
        </p>
      )}
      {actions.canConnect ? (
        <Actions>
          <Button
            variant={link.expired ? 'primary' : 'outline'}
            disabled={actions.issue.isPending}
            onClick={() => {
              actions.issue.mutate();
            }}
          >
            {t('integrations.tool.pending.reissue')}
          </Button>
          <Button
            variant="danger"
            disabled={actions.cancel.isPending}
            onClick={() => {
              actions.cancel.mutate();
            }}
          >
            {t('integrations.tool.pending.cancel')}
          </Button>
        </Actions>
      ) : null}
    </>
  );
}

function ReviewBody({
  provider,
  company,
  link,
  actions,
}: Readonly<{
  provider: ChatProvider;
  company: IntegrationCompany;
  link: Link;
  actions: Actions;
}>) {
  const account = link.account;
  const domains = account?.domains.join(', ') ?? '';
  return (
    <>
      <p className="m-0 rounded-md bg-tone-amber-tint p-3 text-meta text-tone-amber-ink">
        {t(`integrations.${provider}.review.warning`, { domains, group: company.name })}
      </p>
      <KvGrid>
        <Kv label={t(`integrations.${provider}.account`)}>{account?.name ?? account?.id ?? ''}</Kv>
        <Kv label={t('integrations.tool.domains')} empty={!domains}>
          {domains || t('integrations.tool.noDomains')}
        </Kv>
        {account?.authorizedBy ? (
          <Kv label={t('integrations.tool.authorizedBy')}>{account.authorizedBy}</Kv>
        ) : null}
        <Kv label={t('integrations.tool.expectedDomains')} empty={!company.emailDomains.length}>
          {company.emailDomains.join(', ') || t('integrations.tool.noDomains')}
        </Kv>
        <Kv label={t('integrations.tool.authorizedAt')}>
          {link.authorizedAt ? formatDateTime(link.authorizedAt) : ''}
        </Kv>
      </KvGrid>
      {actions.canApprove ? (
        <Actions>
          <Button
            variant="primary"
            disabled={actions.confirm.isPending}
            onClick={() => {
              actions.confirm.mutate();
            }}
          >
            {t('integrations.tool.review.confirm')}
          </Button>
          <Button
            variant="danger"
            disabled={actions.reject.isPending}
            onClick={() => {
              actions.reject.mutate();
            }}
          >
            {t('integrations.tool.review.reject')}
          </Button>
        </Actions>
      ) : (
        <p className="m-0 text-meta text-muted-foreground">
          {t('integrations.access.approveOnly')}
        </p>
      )}
    </>
  );
}

function ConnectedBody({
  provider,
  installation,
  actions,
}: Readonly<{ provider: ChatProvider; installation: Installation; actions: Actions }>) {
  const [confirming, setConfirming] = useState(false);
  const { account } = installation;
  return (
    <>
      <KvGrid>
        <Kv label={t(`integrations.${provider}.account`)}>{account.name ?? account.id}</Kv>
        <Kv label={t(`integrations.${provider}.accountId`)} mono>
          {account.id}
        </Kv>
        {account.domains.length > 0 ? (
          <Kv label={t('integrations.tool.domains')}>{account.domains.join(', ')}</Kv>
        ) : null}
        <Kv label={t('integrations.tool.connectedAt')}>
          {formatDateTime(installation.installedAt)}
        </Kv>
      </KvGrid>
      {actions.canApprove ? (
        confirming ? (
          <Actions>
            <span className="self-center text-meta text-foreground">
              {t('integrations.tool.connected.confirmText', { tool: TOOL[provider].name() })}
            </span>
            <Button
              variant="danger"
              disabled={actions.disconnect.isPending}
              onClick={() => {
                actions.disconnect.mutate();
              }}
            >
              {t('integrations.tool.connected.disconnect')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('integrations.tool.connected.keep')}
            </Button>
          </Actions>
        ) : (
          <Actions>
            <Button
              variant="danger"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('integrations.tool.connected.disconnect')}
            </Button>
          </Actions>
        )
      ) : null}
    </>
  );
}

function NoneBody({
  provider,
  blockedBy,
  actions,
}: Readonly<{ provider: ChatProvider; blockedBy: ChatProvider | null; actions: Actions }>) {
  return (
    <>
      <p className="m-0 text-meta text-muted-foreground">
        {t(`integrations.${provider}.none.desc`)}
      </p>
      {blockedBy ? (
        <p className="m-0 text-meta text-muted-foreground">
          {t('integrations.tool.none.blocked', { tool: TOOL[blockedBy].name() })}
        </p>
      ) : null}
      {actions.canConnect ? (
        <Actions>
          <Button
            variant="primary"
            disabled={actions.issue.isPending || blockedBy !== null}
            onClick={() => {
              actions.issue.mutate();
            }}
          >
            <AppIcons name="link-simple" size={14} />
            {t('integrations.tool.none.issue')}
          </Button>
        </Actions>
      ) : null}
    </>
  );
}

function ToolBody({
  provider,
  company,
  issued,
  blockedBy,
  actions,
}: Readonly<{
  provider: ChatProvider;
  company: IntegrationCompany;
  issued: ChatConnectLinkCreateResponse | null;
  blockedBy: ChatProvider | null;
  actions: Actions;
}>) {
  const state = company[provider];
  switch (state.status) {
    case 'unavailable':
      return (
        <p className="m-0 text-meta text-muted-foreground">{t('integrations.tool.unavailable')}</p>
      );
    case 'connected':
      return (
        <ConnectedBody provider={provider} installation={state.installation} actions={actions} />
      );
    case 'review':
      return (
        <ReviewBody provider={provider} company={company} link={state.link} actions={actions} />
      );
    case 'pending': {
      const revealed =
        issued?.link.createdAt === state.link.createdAt ? (
          <LinkReveal
            provider={provider}
            company={company}
            issued={issued}
            showSnackbar={actions.showSnackbar}
          />
        ) : null;
      return <PendingBody link={state.link} revealed={revealed} actions={actions} />;
    }
    case 'none':
    default:
      return <NoneBody provider={provider} blockedBy={blockedBy} actions={actions} />;
  }
}

/** One chat tool's checklist item for a corporate group (Teams or Slack). */
export function ChatToolCard({
  provider,
  company,
  blockedBy,
}: Readonly<{
  provider: ChatProvider;
  company: IntegrationCompany;
  /** The other chat tool already in use: one chat tool per company. */
  blockedBy: ChatProvider | null;
}>) {
  // The URL exists only in the issue response (the connector keeps a hash): keep
  // it for this card while the operator copies it.
  const [issued, setIssued] = useState<ChatConnectLinkCreateResponse | null>(null);
  const actions = useToolActions(provider, company.id, setIssued);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border-soft bg-surface p-3.5">
      <div className="flex items-center gap-2.5">
        <AppIcons name={TOOL[provider].icon} size={18} />
        <span className="font-semibold text-foreground">{TOOL[provider].name()}</span>
        <span className="ml-auto">
          <ChatStatusChip state={chipStateOf(company[provider])} />
        </span>
      </div>
      <ToolBody
        provider={provider}
        company={company}
        issued={issued}
        blockedBy={blockedBy}
        actions={actions}
      />
    </div>
  );
}
